import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useBillingInfo } from '~/billing/hooks/useBillingInfo'
import { Button } from '~/components/common/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { InitialsAvatar } from '~/components/common/InitialsAvatar'
import { ThemeToggle } from '~/components/common/ThemeToggle'
import { Typography } from '~/components/common/Typography'
import { useBillingAccounts } from '~/contexts/BillingAccountsContext'
import { useBillingContext } from '~/contexts/BillingContext'
import { useImpersonation } from '~/contexts/ImpersonationContext'
import { useModalContext } from '~/contexts/ModalContext'
import { useUser } from '~/hooks'
import { bridge } from '~/services/bridge'
import { performLogout } from '~/services/bridge/pluginAuth'

export function SidebarMenu() {
  const billingInfo = useBillingInfo()
  const { isBillingAdmin, permittedCommands } = useBillingContext()
  const { openPlan, openManageAccount, openAccount, openPersonalUsage, openMonitoringDashboard } =
    useModalContext()
  const { billingAccounts } = useBillingAccounts()
  const { isImpersonating, stopImpersonation } = useImpersonation()
  const isPlugin = bridge.isInPlugin()

  const user = useUser()
  const navigate = useNavigate()
  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()
  const [open, setOpen] = useState<boolean>(false)

  const handleSettingsClick = () => {
    navigate(`/w/${workspaceUuid}/settings`)
    setOpen(false)
  }

  const handleSignOutClick = async () => {
    if (isImpersonating) {
      await stopImpersonation()
      return
    }
    await performLogout()
  }

  return (
    <div className='border-t border-tokens-neutral-border-2 bg-tokens-neutral-bg-white px-6 py-5'>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <div
            className='user-profile flex items-center gap-6 cursor-pointer min-w-0'
            aria-label='User profile dropdown'
          >
            <div className='shrink-0'>
              <InitialsAvatar name={user?.name ?? 'Curie'} size='sm' />
            </div>
            <Typography
              fontFamily='geist'
              fontSize='bodyMdMedium'
              fontWeight='medium'
              className='min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-tokens-neutral-text-primary select-none'
            >
              {user?.name}
            </Typography>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side='top'
          align={isPlugin ? 'center' : 'start'}
          sideOffset={8}
          className={`flex w-[256px] ${isPlugin ? 'max-w-[calc(100vw-24px)]' : ''} pl-2 pr-2 pt-4 pb-4 flex-col justify-end gap-2 rounded-[24px] bg-tokens-neutral-bg-white shadow-[0_6px_16px_0_rgba(0,0,0,0.08),0_3px_6px_-4px_rgba(0,0,0,0.12),0_9px_28px_8px_rgba(0,0,0,0.05)] select-none`}
        >
          <div
            className='hover:!bg-transparent focus:bg-transparent h-auto'
            style={{
              display: 'flex',
              padding: '8px',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              alignSelf: 'stretch'
            }}
          >
            <InitialsAvatar name={user?.name ?? 'Curie'} size='sm' />
            <div className='!h-[22px] !mt-0 !mb-0'>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='medium'
                className='text-tokens-neutral-text-primary'
              >
                {user?.name ?? 'User'}
              </Typography>
            </div>
            <div className='w-full flex justify-center mt-0'>
              <Button
                variant='outlined'
                onClick={handleSettingsClick}
                className='w-[89px] h-[28px] px-4 font-normal'
                style={{ borderRadius: '8px', padding: '6px 20px' }}
                aria-label='Settings button'
              >
                <Typography
                  fontFamily='geist'
                  fontSize='bodySm'
                  fontWeight='medium'
                  style={{ color: 'inherit' }}
                >
                  Settings
                </Typography>
              </Button>
            </div>
          </div>

          <DropdownMenuSeparator className='!mt-0 !mb-0' />
          <div className='!h-[14px] !mt-0 !mb-0'>
            <Typography
              fontFamily='geistMono'
              fontSize='labelSm'
              fontWeight='semibold'
              className='text-tokens-neutral-text-tertiary text-left ml-2 mt-0 tracking-[0.01em]'
            >
              {billingInfo?.name}
            </Typography>
          </div>
          {isBillingAdmin &&
            !permittedCommands?.active_license_types?.some(t => t.includes('enterprise')) && (
              <DropdownMenuItem
                className='!h-[32px] !mt-0 !pl-4 w-full cursor-pointer'
                onClick={openPlan}
                aria-label='Upgrade plan item'
              >
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMd'
                  fontWeight='medium'
                  style={{ color: 'inherit' }}
                >
                  Upgrade Plan
                </Typography>
              </DropdownMenuItem>
            )}
          <DropdownMenuItem
            className='!h-[32px] !mt-0 !pl-4 w-full cursor-pointer'
            onClick={openPersonalUsage}
            aria-label='My Usage item'
          >
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              fontWeight='medium'
              style={{ color: 'inherit' }}
            >
              My Usage
            </Typography>
          </DropdownMenuItem>
          <DropdownMenuItem
            className='!h-[32px] !mt-0 !pl-4 w-full cursor-pointer'
            onClick={openMonitoringDashboard}
            aria-label='Monitoring dashboard item'
          >
            <div className='flex items-center gap-2'>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='medium'
                style={{ color: 'inherit' }}
              >
                Monitoring Dashboard
              </Typography>
              <Typography
                fontFamily='geistMono'
                fontSize='labelSm'
                fontWeight='semibold'
                className='rounded-[6px] bg-tokens-primary-4 px-1.5 py-0.5 text-tokens-neutral-bg-white'
              >
                Beta
              </Typography>
            </div>
          </DropdownMenuItem>
          {isBillingAdmin && (
            <DropdownMenuItem
              className='!h-[32px] !mt-0 !pl-4 w-full cursor-pointer'
              onClick={openManageAccount}
              aria-label='Manage Account item'
            >
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='medium'
                style={{ color: 'inherit' }}
              >
                Manage Account
              </Typography>
            </DropdownMenuItem>
          )}
          {billingAccounts && billingAccounts?.length > 1 && (
            <DropdownMenuItem
              className='!h-[32px] !mt-0 !pl-4 w-full cursor-pointer'
              onClick={openAccount}
            >
              <div className='w-full flex justify-between items-center mr-4'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMd'
                  fontWeight='medium'
                  style={{ color: 'inherit' }}
                >
                  Switch Account
                </Typography>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='7'
                  height='12'
                  viewBox='0 0 7 12'
                  fill='none'
                >
                  <path
                    d='M6.35403 6.35354L1.35403 11.3535C1.30757 11.4 1.25242 11.4368 1.19173 11.462C1.13103 11.4871 1.06598 11.5001 1.00028 11.5001C0.934581 11.5001 0.869526 11.4871 0.80883 11.462C0.748133 11.4368 0.692983 11.4 0.646528 11.3535C0.600073 11.3071 0.563222 11.2519 0.538081 11.1912C0.51294 11.1305 0.5 11.0655 0.5 10.9998C0.5 10.9341 0.51294 10.869 0.538081 10.8083C0.563222 10.7476 0.600073 10.6925 0.646528 10.646L5.2934 5.99979L0.646528 1.35354C0.552707 1.25972 0.5 1.13247 0.5 0.99979C0.5 0.867108 0.552707 0.73986 0.646528 0.64604C0.740348 0.552219 0.867596 0.499512 1.00028 0.499512C1.13296 0.499512 1.26021 0.552219 1.35403 0.64604L6.35403 5.64604C6.40052 5.69248 6.4374 5.74762 6.46256 5.80832C6.48772 5.86902 6.50067 5.93408 6.50067 5.99979C6.50067 6.0655 6.48772 6.13056 6.46256 6.19126C6.4374 6.25196 6.40052 6.3071 6.35403 6.35354Z'
                    fill='black'
                    fillOpacity='0.88'
                  />
                </svg>
              </div>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator className='!mt-0 !mb-0' />
          <div className='px-2 pb-1' onClick={e => e.stopPropagation()}>
            <ThemeToggle />
          </div>
          <DropdownMenuSeparator className='!mt-0 !mb-0' />

          <DropdownMenuItem className='!mt-0 !mb-0 !w-full !px-4' onClick={handleSignOutClick}>
            <div className='flex justify-between cursor-pointer'>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='medium'
                style={{
                  color: isImpersonating ? 'var(--warning-500)' : 'var(--status-error-1, #D32F2F)'
                }}
              >
                {isImpersonating ? 'Exit Impersonation' : 'Log out'}
              </Typography>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
/**
 * Minimal version of SidebarMenu for use when no workspace is available.
 * Shows: user profile, account name, switch account, manage account, and logout.
 * Does NOT show: settings button (requires workspace), my usage, upgrade plan.
 */
export function SidebarMenuMinimal() {
  const billingInfo = useBillingInfo()
  const { isBillingAdmin } = useBillingContext()
  const { openManageAccount, openAccount } = useModalContext()
  const { billingAccounts } = useBillingAccounts()
  const { isImpersonating, stopImpersonation } = useImpersonation()
  const isPlugin = bridge.isInPlugin()

  const user = useUser()
  const [open, setOpen] = useState<boolean>(false)

  const handleSignOutClick = async () => {
    if (isImpersonating) {
      await stopImpersonation()
      return
    }
    await performLogout()
  }

  return (
    <div className='border-t border-tokens-neutral-border-2 bg-tokens-neutral-bg-white px-6 py-5'>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <div
            className='user-profile flex items-center gap-6 cursor-pointer min-w-0'
            aria-label='User profile dropdown'
          >
            <div className='shrink-0'>
              <InitialsAvatar name={user?.name ?? 'Curie'} size='sm' />
            </div>
            <Typography
              fontFamily='geist'
              fontSize='bodyMdMedium'
              fontWeight='medium'
              className='min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-tokens-neutral-text-primary'
            >
              {user?.name}
            </Typography>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side='top'
          align={isPlugin ? 'center' : 'start'}
          sideOffset={8}
          className={`flex w-[256px] ${isPlugin ? 'max-w-[calc(100vw-24px)]' : ''} pl-2 pr-2 pt-4 pb-4 flex-col justify-end gap-2 rounded-[24px] bg-tokens-neutral-bg-white shadow-[0_6px_16px_0_rgba(0,0,0,0.08),0_3px_6px_-4px_rgba(0,0,0,0.12),0_9px_28px_8px_rgba(0,0,0,0.05)] select-none`}
        >
          <div
            className='hover:!bg-transparent focus:bg-transparent h-auto'
            style={{
              display: 'flex',
              padding: '8px',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              alignSelf: 'stretch'
            }}
          >
            <InitialsAvatar name={user?.name ?? 'Curie'} size='sm' />
            <div className='!h-[22px] !mt-0 !mb-0'>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='medium'
                className='select-none text-tokens-neutral-text-primary'
              >
                {user?.name ?? 'User'}
              </Typography>
            </div>
          </div>

          <DropdownMenuSeparator className='!mt-0 !mb-0' />
          <div className='!h-[14px] !mt-0 !mb-0'>
            <Typography
              fontFamily='geistMono'
              fontSize='labelSm'
              fontWeight='semibold'
              className='text-tokens-neutral-text-tertiary text-left ml-2 mt-0 tracking-[0.01em]'
            >
              {billingInfo?.name}
            </Typography>
          </div>
          {isBillingAdmin && (
            <DropdownMenuItem
              className='!h-[32px] !mt-0 !pl-4 w-full cursor-pointer'
              onClick={openManageAccount}
              aria-label='Manage Account item'
            >
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='medium'
                style={{ color: 'inherit' }}
              >
                Manage Account
              </Typography>
            </DropdownMenuItem>
          )}
          {billingAccounts && billingAccounts?.length > 1 && (
            <DropdownMenuItem
              className='!h-[32px] !mt-0 !pl-4 w-full cursor-pointer'
              onClick={openAccount}
            >
              <div className='w-full flex justify-between items-center mr-4'>
                <Typography
                  fontFamily='geist'
                  fontSize='bodyMd'
                  fontWeight='medium'
                  style={{ color: 'inherit' }}
                >
                  Switch Account
                </Typography>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='7'
                  height='12'
                  viewBox='0 0 7 12'
                  fill='none'
                >
                  <path
                    d='M6.35403 6.35354L1.35403 11.3535C1.30757 11.4 1.25242 11.4368 1.19173 11.462C1.13103 11.4871 1.06598 11.5001 1.00028 11.5001C0.934581 11.5001 0.869526 11.4871 0.80883 11.462C0.748133 11.4368 0.692983 11.4 0.646528 11.3535C0.600073 11.3071 0.563222 11.2519 0.538081 11.1912C0.51294 11.1305 0.5 11.0655 0.5 10.9998C0.5 10.9341 0.51294 10.869 0.538081 10.8083C0.563222 10.7476 0.600073 10.6925 0.646528 10.646L5.2934 5.99979L0.646528 1.35354C0.552707 1.25972 0.5 1.13247 0.5 0.99979C0.5 0.867108 0.552707 0.73986 0.646528 0.64604C0.740348 0.552219 0.867596 0.499512 1.00028 0.499512C1.13296 0.499512 1.26021 0.552219 1.35403 0.64604L6.35403 5.64604C6.40052 5.69248 6.4374 5.74762 6.46256 5.80832C6.48772 5.86902 6.50067 5.93408 6.50067 5.99979C6.50067 6.0655 6.48772 6.13056 6.46256 6.19126C6.4374 6.25196 6.40052 6.3071 6.35403 6.35354Z'
                    fill='black'
                    fillOpacity='0.88'
                  />
                </svg>
              </div>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator className='!mt-0 !mb-0' />
          <div className='px-2 pb-1' onClick={e => e.stopPropagation()}>
            <ThemeToggle />
          </div>
          <DropdownMenuSeparator className='!mt-0 !mb-0' />

          <DropdownMenuItem className='!mt-0 !mb-0 !w-full !px-4' onClick={handleSignOutClick}>
            <div className='flex justify-between cursor-pointer'>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                fontWeight='medium'
                style={{
                  color: isImpersonating ? 'var(--warning-500)' : 'var(--status-error-1, #D32F2F)'
                }}
              >
                {isImpersonating ? 'Exit Impersonation' : 'Log out'}
              </Typography>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default SidebarMenu
